// @flow
import React, {Component} from 'react';
import './App.css';
import DataMap from './DataMap';
import CandidateSelect from './CandidateSelect';
import Legend from './Legend';
import CandidateTracker from './CandidateTracker';
import Navbar from './Navbar';
import AboutModal from './AboutModal';
import FlexModalWrapper from 'react-modal-wrapper';
import bs from 'binarysearch';
import _ from 'lodash';
import Youtube from 'react-youtube';
import {DECAY, ENDPOINTS, PLAYER_STATES, STATES, VIDEOS, YT_API_KEY} from './constants';
import type {Candidate, CensusData, DataSet, TimeSeries} from './types';

type State = {
  aboutModalOpened: boolean,
  candidateSelected: 'both' | Candidate,
  censusData: CensusData,
  error: ?Error,
  liveStreamingDetails: ?{actualStartTime: string, actualEndTime?: string, scheduledStartTime: string},
  player: ?Object,
  playerPosition: number,
  playerState: number,
  selectedInterval: number,
  timeNums: number[],
  timeSeries: TimeSeries,
  videoData: {title: string},
  videoID: string,
};

const DEV = window.location.hostname === 'localhost' && false;
const PDEBATE3_START = new Date("Wed, 19 Oct 2016 01:00:00 GMT");
const ONE_HOUR = 1000 * 60 * 60;
const START_VIDEO = (PDEBATE3_START - Date.now() < ONE_HOUR) ? VIDEOS.PDebate3 : VIDEOS.PDebate2;

export default class App extends Component {

  state: State = {
    error: null,
    timeSeries: {},
    censusData: {},
    candidateSelected: 'both',
    liveStreamingDetails: null,
    player: null,
    playerPosition: 0,
    playerState: -1,
    timeNums: [],
    selectedInterval: 0,
    videoData: {title: ''},
    videoID: '',
    aboutModalOpened: false
  };

  ws: WebSocket;

  componentDidMount() {
    this.setVideo(START_VIDEO);
    setInterval(() => this.trackPlayerPosition(), 1000);
    this.openWS();
    this.getCensusData();
  }

  getDataSet(): DataSet {
    const {selectedInterval, timeSeries} = this.state;
    return timeSeries[String(selectedInterval)];
  }

  async getCensusData() {
    const result = await (
      await fetch(ENDPOINTS.census)
    ).json();
    this.setState({censusData: result});
  }

  getPlayerTime(playerPosition: number): Date {
    if (!this.state.liveStreamingDetails) return new Date();
    return new Date(new Date(this.state.liveStreamingDetails.actualStartTime).valueOf() + (playerPosition * 1000));
  }

  setVideo(videoID: string) {
    this.setState({videoID}, () => {
      document.body.scrollTop = 0;
      this.fetchYTData().then(() => {
        this.fetchData(this.state.videoData);
      });
    });
  }

  onPlayerStateChange = (event: Object) => {
    if (!this.state.player) {
      this.fetchData();
    }
    const player = event.target;
    this.setState({playerState: event.data, player, videoData: player.getVideoData()});
  };

  onSetCandidate = (candidate: $PropertyType<State, 'candidateSelected'>) => {
    this.setState({candidateSelected: candidate});
  };

  // Go through data - if data for a particular bin is 0, backfill with last bin's data.
  // This is because the API only returns data if it changes.
  poopSweep(timeSeries: TimeSeries, keys: number[], idx: number = 1) {
    // SPEEEEEEED
    if (keys.length < 2) return timeSeries;
    for (let i = idx; i < keys.length; i++) {
      const series = timeSeries[String(keys[i])];
      const lastSeries = timeSeries[String(keys[i - 1])];
      for (let j = 0; j < STATES.length; j++) {
        const state = STATES[j];
        if (!series[state]) series[state] = {hillary: 0, trump: 0};
        if (!lastSeries[state]) lastSeries[state] = {hillary: 0, trump: 0};
        series[state].hillary += (lastSeries[state].hillary * DECAY);
        series[state].trump += (lastSeries[state].trump * DECAY);
      }
    }
    return timeSeries;
  }

  async fetchData(): Promise<void> {
    if (!this.state.liveStreamingDetails) throw new Error("Get video details first!");
    try {
      const startTime = new Date(this.state.liveStreamingDetails.actualStartTime);
      const endTime = new Date(this.state.liveStreamingDetails.actualEndTime || Date.now());
      const timeSeries: TimeSeries = await (
        await fetch(
          `${ENDPOINTS.http}?start=${Math.floor(startTime / 1000)}&end=${Math.floor(endTime / 1000)}`,
          {mode: 'cors'})
      ).json();
      const timeNums = Object.keys(timeSeries).map(Number).sort((a, b) => a - b);
      this.setState({
        selectedInterval: Math.floor(startTime.valueOf() / 1000),
        timeSeries: this.poopSweep(timeSeries, timeNums),
        timeNums
      });
    } catch (error) {
      this.setState({error});
    }
  }

  async fetchYTData() {
    const result = await (
      await fetch(`https://content.googleapis.com/youtube/v3/videos` +
        `?part=liveStreamingDetails` + // add ,snippet for some more data
        `&id=${this.state.videoID}` +
        `&key=${YT_API_KEY}`)
    ).json();
    const {liveStreamingDetails} = result.items[0];
    this.setState({liveStreamingDetails});
  }

  openWS() {
    this.ws = new WebSocket(ENDPOINTS.ws);
    this.ws.onmessage = (msg: Object) => {
      const {timeNums, timeSeries} = this.state;
      console.log(msg.data);
      try {
        const datum: TimeSeries = JSON.parse(msg.data);
        const key = Object.keys(datum)[0];
        const newTimeNums = timeNums.concat([Number(key)]);
        this.setState({
          timeSeries: this.poopSweep({...timeSeries, [key]: datum[key]}, newTimeNums, newTimeNums.length - 1),
          timeNums: newTimeNums
        });
      } catch (error) {
        console.error(error);
      }
    };
  }

  trackPlayerPosition() {
    const {liveStreamingDetails, player, timeNums} = this.state;
    if (!player) return;
    const playerPosition = player.getCurrentTime(); // seconds
    // Set interval to the nearest from this position
    if (!liveStreamingDetails) throw new Error('Start time not found.');
    const startTimeSec = Math.floor(new Date(liveStreamingDetails.actualStartTime).valueOf() / 1000);
    // Get closest tick to this tick using binary search
    const intervalIdx = bs.closest(timeNums, startTimeSec + playerPosition);
    const selectedInterval = timeNums[intervalIdx];
    this.setState({playerPosition, selectedInterval});
  }

  openAboutModal = () => {
    this.setState({aboutModalOpened: true});
  };

  closeAboutModal = () => {
    this.setState({aboutModalOpened: false});
  };

  render() {
    const {candidateSelected, error, playerPosition, playerState, videoID, censusData} = this.state;
    const thisData = this.getDataSet();
    return (
      <div className="App">
        <Navbar onAboutButtonClick={this.openAboutModal}/>
        <div className="section player">
          <div className="flex-container">
            <CandidateTracker data={thisData} />
            <div className="flex-item video-wrapper card">
              <Youtube
                className="propaganda-machine"
                videoId={videoID}
                onStateChange={this.onPlayerStateChange}
                opts={{
                  playerVars: {
                    autoplay: 1
                  }
                }}
              />
              {DEV ?
                <pre><code>
                  Status: {PLAYER_STATES[playerState]}&nbsp;&nbsp;
                  Time: {this.getPlayerTime(playerPosition).toLocaleString()}
                </code></pre>
                : /* <div className="video-title">{videoData.title}</div> */ null
              }
            </div>
          </div>
        </div>
        <div className="section map">
          <div className="flex-container">
            <div className="flex-item map-wrapper">
              <div className="map-info">
                <CandidateSelect candidateSelected={candidateSelected} setCandidate={this.onSetCandidate}/>
                <Legend />
              </div>
              {error ?
                <pre><code>{error}</code></pre>
                : <DataMap data={thisData}
                           candidateSelected={candidateSelected}
                           censusData={censusData}/>}
            </div>
          </div>
        </div>
        <div className="section choose-video">
          <h4>About the Real-Time Debate Scorecard</h4>
          <div className="flex-container">
            <div className="flex-item card">
              <p>
                DigitalCandy has taken the data streams of Twitter and Instagram during the election and run sentiment analysis on it by state.
              </p>
              <p>
                The map above shows real-time sentiment analysis of the social web for the debate. See the states respond in realtime to the candidates' actions and words!
              </p>
            </div>
          </div>
        </div>
        <div className="section choose-video">
          <h4>Choose Video</h4>
          <div className="flex-container">
            {_.map(VIDEOS, (value) => (
              <div className={`flex-item card video-thumb ${videoID === value ? 'active' : ''}`}
                   onClick={this.setVideo.bind(this, value)} key={value}>
                <img src={`http://img.youtube.com/vi/${value}/mqdefault.jpg`} alt="video-thumb" />
              </div>
            ))}
          </div>
        </div>
        <FlexModalWrapper closeOnEsc={true} closeOnOutsideClick={true}
                          onClose={this.closeAboutModal} isOpened={this.state.aboutModalOpened}>
          <AboutModal />
        </FlexModalWrapper>
      </div>
    );
  }

}



/** WEBPACK FOOTER **
 ** ./src/App.js
 **/